import { useEffect, useState } from "react";
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";
import { PackageSearch } from "lucide-react";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";
import { ModalWrapper } from "../../../components/AdminSideComponents/common/ModalWrapper";
import api from "../../../configs/api";
import { useNavigate } from "react-router-dom";

const EMPTY_FORM = { name: "", unit: "kg", plant: "" };

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/calculator-products`);
      setProducts(res.data.data);
    } catch {
      showError("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (product) => {
    const confirmed = await confirmAction({
      title: "Delete Product?",
      text: "This action cannot be undone.",
    });
    if (!confirmed) return;
    try {
      await api.delete(`/calculator-products/${product._id}`);
      setProducts((prev) => prev.filter((p) => p._id !== product._id));
      showSuccess("Product deleted");
    } catch {
      showError("Failed to delete product");
    }
  };

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()),
  );

  const navigate = useNavigate();

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-descriptionText">
            <IoIosSearch className="size-4" />
          </span>
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-[42px] w-full sm:w-[25rem] pl-9 pr-4 border border-borderLight outline-none focus:border-primaryAccent text-sm"
          />
        </div>
        <button
          onClick={() => navigate("/admin/admin-calculator-products/add")}
          className="flex items-center gap-2 px-4 py-2 bg-primaryAccent text-white text-sm h-[42px]"
        >
          <FiPlus /> Add Product
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="border border-borderLight rounded-xl p-4 animate-pulse"
            >
              <div className="h-4 bg-divider w-1/2 mb-3" />
              <div className="h-3 bg-divider w-1/3 mb-2" />
              <div className="h-3 bg-divider w-full mb-2" />
              <div className="h-3 bg-divider w-3/4" />
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="col-span-full flex flex-col items-center py-20">
            <PackageSearch size={40} className="text-primaryAccent mb-4" />
            <p className="text-lg font-semibold text-headingText">
              No products found
            </p>
          </div>
        ) : (
          filtered.map((product) => (
            <div
              key={product._id}
              className="border border-borderLight p-4 bg-white shadow-sm hover:shadow-md transition"
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex flex-col gap-2">
                  <p className="text-sm capitalize text-headingText mt-0.5">
                    <span className="font-semibold">Plant Name :</span>{" "}
                    {product.plant?.plantName || "N/A"}
                  </p>
                  <div>
                    <h3 className="text-sm capitalize text-headingText">
                      <span className="font-semibold">Product Name :</span>{" "}
                      {product.name}
                    </h3>
                    <p className="text-sm text-descriptionText mt-1">
                      Unit: {product.unit}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      navigate(
                        `/admin/admin-calculator-products/edit/${product._id}`,
                      )
                    }
                    className="text-primaryAccent hover:opacity-70"
                  >
                    <FiEdit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(product)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-borderLight my-2" />

              <div className="flex flex-col gap-4">
                {/* Varieties */}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-descriptionText mb-1">
                    Varieties
                  </p>

                  {product.variety?.length ? (
                    <div className="flex flex-wrap gap-3">
                      {product.variety.map((v, i) => (
                        <p
                          key={i}
                          className="bg-primaryAccent/10 text-primaryAccent px-2 py-1  text-xs"
                        >
                          {v.name} - ₹{v.price}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No varieties</p>
                  )}
                </div>

                {/* Grades */}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-descriptionText mb-1">
                    Grades
                  </p>

                  {product.gradeMixtures?.length ? (
                    <div className="flex flex-wrap gap-2">
                      {product.gradeMixtures.map((g, i) => (
                        <div
                          key={i}
                          className="bg-primaryAccent/10 text-primaryAccent px-2 py-1  text-xs"
                        >
                          <p className="tracking wide">
                            Grade-{g.grade?.gradeName || "N/A"}
                          </p>
                          <p className="tracking-wide">Qty-{g.quantity}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No grades</p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProductsTab;
